if (!customElements.get('recipient-form')) {
  customElements.define(
    'recipient-form',
    class RecipientForm extends HTMLElement {
      constructor() {
        super();
        this.recipientFieldsLiveRegion = this.querySelector(`#Recipient-fields-live-region-${this.dataset.sectionId}`);
        this.checkboxInput = this.querySelector(`#Recipient-checkbox-${this.dataset.sectionId}`);
        this.checkboxInput.disabled = false;
        this.hiddenControlField = this.querySelector(`#Recipient-control-${this.dataset.sectionId}`);
        this.hiddenControlField.disabled = true;
        this.emailInput = this.querySelector(`#Recipient-email-${this.dataset.sectionId}`);
        this.nameInput = this.querySelector(`#Recipient-name-${this.dataset.sectionId}`);
        this.senderEmailInput = this.querySelector(`#Sender-email-${this.dataset.sectionId}`);
        this.senderNameInput = this.querySelector(`#Sender-name-${this.dataset.sectionId}`);
        this.messageInput = this.querySelector(`#Recipient-message-${this.dataset.sectionId}`);
        this.sendonInput = this.querySelector(`#Recipient-send-on-${this.dataset.sectionId}`);
        this.offsetProperty = this.querySelector(`#Recipient-timezone-offset-${this.dataset.sectionId}`);
        if (this.offsetProperty) this.offsetProperty.value = new Date().getTimezoneOffset().toString();

        this.currentProductVariantId = this.dataset.productVariantId;
        this.validationMessages = {
          recipientEmailRequired: this.dataset.recipientEmailRequired || 'Recipient email is required',
          senderEmailRequired: this.dataset.senderEmailRequired || 'Sender email is required',
          recipientEmailInvalid: this.dataset.emailInvalid || 'Please enter a valid recipient email',
          senderEmailInvalid: this.dataset.senderEmailInvalid || 'Please enter a valid sender email',
          messageRequired: this.dataset.messageRequired || 'Message is required',
        };
        this.addEventListener('change', this.onChange.bind(this));
        this.onChange();
      }

      cartUpdateUnsubscriber = undefined;
      variantChangeUnsubscriber = undefined;
      cartErrorUnsubscriber = undefined;

      connectedCallback() {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event.source === 'product-form' && event.productVariantId.toString() === this.currentProductVariantId) {
            this.resetRecipientForm();
          }
        });

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event.data.sectionId === this.dataset.sectionId) {
            this.currentProductVariantId = event.data.variant.id.toString();
          }
        });

        this.cartErrorUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, (event) => {
          if (event.source === 'product-form' && event.productVariantId.toString() === this.currentProductVariantId) {
            this.displayErrorMessage(event.errors);
          }
        });
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }

        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }

        if (this.cartErrorUnsubscriber) {
          this.cartErrorUnsubscriber();
        }
      }

      onChange() {
        if (this.checkboxInput.checked) {
          this.enableInputFields();
          this.recipientFieldsLiveRegion.innerText = window.accessibilityStrings.recipientFormExpanded;
        } else {
          this.clearInputFields();
          this.disableInputFields();
          this.clearErrorMessage();
          this.recipientFieldsLiveRegion.innerText = window.accessibilityStrings.recipientFormCollapsed;
        }
      }

      inputFields() {
        return [this.emailInput, this.nameInput, this.messageInput, this.sendonInput];
      }

      disableableFields() {
        return [...this.inputFields(), this.offsetProperty];
      }

      clearInputFields() {
        this.inputFields().forEach((field) => (field.value = ''));
      }

      enableInputFields() {
        this.disableableFields().forEach((field) => (field.disabled = false));
      }

      disableInputFields() {
        this.disableableFields().forEach((field) => (field.disabled = true));
      }

      getFieldConfig(key) {
        const sectionId = this.dataset.sectionId;
        const configs = {
          email: {
            input: this.emailInput,
            errorId: `RecipientForm-email-error-${sectionId}`,
            selector: `#Recipient-email-${sectionId}`,
          },
          name: {
            input: this.nameInput,
            errorId: `RecipientForm-name-error-${sectionId}`,
            selector: `#Recipient-name-${sectionId}`,
          },
          message: {
            input: this.messageInput,
            errorId: `RecipientForm-message-error-${sectionId}`,
            selector: `#Recipient-message-${sectionId}`,
          },
          senderEmail: {
            input: this.senderEmailInput,
            errorId: `SenderForm-email-error-${sectionId}`,
            selector: `#Sender-email-${sectionId}`,
          },
          senderName: {
            input: this.senderNameInput,
            errorId: `SenderForm-name-error-${sectionId}`,
            selector: `#Sender-name-${sectionId}`,
          },
        };

        return configs[key];
      }

      validateEmailField(input, requiredMessage, invalidMessage) {
        const value = input?.value?.trim() ?? '';
        if (!value) return requiredMessage;
        if (!input.checkValidity()) return invalidMessage;
        return null;
      }

      validateRequiredField(input, requiredMessage) {
        const value = input?.value?.trim() ?? '';
        if (!value) return requiredMessage;
        return null;
      }

      validate() {
        if (!this.checkboxInput.checked) return true;

        this.clearErrorMessage();

        const errors = {};
        const {
          recipientEmailRequired,
          senderEmailRequired,
          recipientEmailInvalid,
          senderEmailInvalid,
          messageRequired,
        } = this.validationMessages;

        const recipientEmailError = this.validateEmailField(
          this.emailInput,
          recipientEmailRequired,
          recipientEmailInvalid
        );
        if (recipientEmailError) errors.email = [recipientEmailError];

        const senderEmailError = this.validateEmailField(
          this.senderEmailInput,
          senderEmailRequired,
          senderEmailInvalid
        );
        if (senderEmailError) errors.senderEmail = [senderEmailError];

        const messageError = this.validateRequiredField(this.messageInput, messageRequired);
        if (messageError) errors.message = [messageError];

        if (Object.keys(errors).length) {
          this.displayErrorMessage(errors);
          return false;
        }

        return true;
      }

      displayErrorMessage(body) {
        this.clearErrorMessage();

        if (typeof body !== 'object') return;

        let firstInvalidInput = null;

        Object.entries(body).forEach(([key, value]) => {
          const config = this.getFieldConfig(key);
          if (!config) return;

          const message = `${value.join(', ')}`;
          const errorMessageElement = this.querySelector(`#${config.errorId}`);
          const errorTextElement = errorMessageElement?.querySelector('.error-message');
          if (!errorTextElement) return;

          errorTextElement.innerText = `${message}.`;
          errorMessageElement.classList.remove('hidden');

          const inputElement = config.input;
          if (!inputElement) return;

          inputElement.setAttribute('aria-invalid', true);
          inputElement.setAttribute('aria-describedby', config.errorId);

          if (!firstInvalidInput) firstInvalidInput = inputElement;
        });

        firstInvalidInput?.focus();
      }

      clearErrorMessage() {
        this.querySelectorAll('.recipient-form__field-error').forEach((field) => {
          field.classList.add('hidden');
          const textField = field.querySelector('.error-message');
          if (textField) textField.innerText = '';
        });

        [this.emailInput, this.messageInput, this.nameInput, this.sendonInput, this.senderEmailInput, this.senderNameInput]
          .filter(Boolean)
          .forEach((inputElement) => {
            inputElement.setAttribute('aria-invalid', false);
            inputElement.removeAttribute('aria-describedby');
          });
      }

      resetRecipientForm() {
        if (this.checkboxInput.checked) {
          this.checkboxInput.checked = false;
          this.clearInputFields();
          this.clearErrorMessage();
        }
      }
    }
  );
}
